import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  REGIONS,
  ZERO_OFFSETS,
  cellAt,
  fillOrder,
  type FillKind,
  type Region,
  type Side,
  type SideOffsets,
  stripeOf,
} from '@/engine';

interface Props {
  /** first coloured hand on each side (0-based, reading order) */
  from?: SideOffsets;
  /** number of coloured hands */
  b: number;
  /** which triangle(s) light up: offsuit (lower), suited (upper) or both */
  side?: Side;
  /** which hands are in play (only these can light up) */
  region?: Region;
  /** stripe region: the rank whose 12 hands are in play (A column when omitted) */
  rank?: number | undefined;
  /** lighting order: column by column (default) or by diagonals */
  fill?: FillKind;
  /** run the order from its far end */
  reversed?: boolean;
  /** thumbnail: no click zoom, no wrapper */
  static?: boolean;
}

/** 13×13 hand grid: offsuit hands in the lower triangle, suited in the upper (same two-letter labels), pairs on the diagonal. */
export const RangeMatrix = memo(function RangeMatrix({
  from = ZERO_OFFSETS,
  b,
  side = 'both',
  region = 'full',
  rank: stripeRank,
  fill = 'column',
  reversed = false,
  static: isStatic = false,
}: Props) {
  const [zoom, setZoom] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // a click anywhere else closes the zoom
  useEffect(() => {
    if (!zoom) return;
    const off = (e: PointerEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setZoom(false);
    };
    document.addEventListener('pointerdown', off);
    return () => document.removeEventListener('pointerdown', off);
  }, [zoom]);
  // each playable hand's position in the lighting sequence (the order filtered to the region);
  // a stripe has its own order: from its A end to its 2 end (or back), whatever the fill kind
  const rank = useMemo(() => {
    const m = new Map<number, number>();
    if (region === 'stripe') {
      const order = stripeOf(stripeRank ?? 0);
      for (const k of reversed ? order.reverse() : order) m.set(k, m.size);
      return m;
    }
    const allowed = new Set<number>(REGIONS[region]);
    for (const k of fillOrder(fill, reversed)) if (allowed.has(k)) m.set(k, m.size);
    return m;
  }, [region, stripeRank, fill, reversed]);
  const cells = [];
  for (let r = 0; r < 13; r++) {
    for (let c = 0; c < 13; c++) {
      const cell = cellAt(r, c);
      if (cell.side === 'pair') {
        cells.push(
          <span key={`${r}-${c}`} className="cell empty">
            {cell.label}
          </span>,
        );
        continue;
      }
      const k = rank.get(cell.index);
      const lit = side === 'both' || side === cell.side;
      const start = from[cell.side];
      const on = k !== undefined && lit && k >= start && k < start + b;
      const cls = on ? 'cell on' : `cell ${cell.side}`;
      cells.push(
        <span key={`${r}-${c}`} className={cls}>
          {cell.label}
        </span>,
      );
    }
  }
  const grid = (
    <div
      ref={ref}
      className={isStatic ? 'matrix static' : `matrix${zoom ? ' zoom' : ''}`}
      role="img"
      aria-label={String(b)}
      tabIndex={isStatic ? undefined : 0}
      onClick={isStatic ? undefined : () => setZoom((z) => !z)}
      onKeyDown={
        isStatic
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setZoom((z) => !z);
              }
            }
      }
    >
      {cells}
    </div>
  );
  return isStatic ? grid : <div className="mwrap">{grid}</div>;
});
